import type { ExecutionTradeOverlay } from "../types/trade";

export type SimulatedTradeRow = ExecutionTradeOverlay & {
  delta?: Record<string, unknown>;
  baseline?: Partial<ExecutionTradeOverlay> & Record<string, unknown>;
  simulated?: Partial<ExecutionTradeOverlay> & Record<string, unknown>;
  applied_actions?: Array<Record<string, unknown>>;
};

export type SimulationChangeInfo = {
  changed: boolean;
  baseline?: ExecutionTradeOverlay & Record<string, unknown>;
  simulated: ExecutionTradeOverlay & Record<string, unknown>;
  delta?: Record<string, unknown>;
};

export function normalizeTradeOverlayId(id: string | null | undefined): string {
  return String(id ?? "")
    .trim()
    .toLowerCase();
}

function finitePrice(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function comparableValue(v: unknown): string {
  if (v == null || v === "") return "";
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  if (typeof v === "string") {
    const asNum = Number(v);
    if (Number.isFinite(asNum) && v.trim() !== "") return String(asNum);
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
    return v.trim();
  }
  return String(v);
}

function isNestedManagementSimRow(row: SimulatedTradeRow): boolean {
  const nested = row.simulated;
  return Boolean(nested && typeof nested === "object" && !Array.isArray(nested));
}

function stripSlTpFromOverlay<T extends Record<string, unknown>>(row: T | undefined): Partial<T> {
  if (!row || typeof row !== "object") return {};
  const { sl_price: _sl, tp_price: _tp, ...rest } = row;
  return rest as Partial<T>;
}

export function unwrapSimulatedTradeRow(row: SimulatedTradeRow): {
  baseline?: ExecutionTradeOverlay & Record<string, unknown>;
  simulated: ExecutionTradeOverlay & Record<string, unknown>;
  delta?: Record<string, unknown>;
  tradeId: string | null;
} {
  const tradeId = (row.trade_id ?? null) as string | null;
  if (isNestedManagementSimRow(row)) {
    const nestedBase = row.baseline;
    const nestedSim = row.simulated!;
    return {
      baseline: nestedBase as ExecutionTradeOverlay & Record<string, unknown> | undefined,
      simulated: {
        ...stripSlTpFromOverlay(
          nestedBase as ExecutionTradeOverlay & Record<string, unknown> | undefined
        ),
        ...nestedSim,
        trade_id: tradeId,
        sl_price: nestedSim.sl_price ?? null,
        tp_price: nestedSim.tp_price ?? null,
      } as ExecutionTradeOverlay & Record<string, unknown>,
      delta: row.delta,
      tradeId,
    };
  }
  return {
    simulated: row as ExecutionTradeOverlay & Record<string, unknown>,
    delta: row.delta,
    tradeId,
  };
}

export function isSimulationTradeChanged(
  baseline: (ExecutionTradeOverlay & Record<string, unknown>) | undefined,
  simulated: ExecutionTradeOverlay & Record<string, unknown>,
  delta?: Record<string, unknown>
): boolean {
  if (delta) {
    if (delta.exit_reason_changed === true) return true;
    if (delta.exit_reason_changed === false) {
      const pnlDelta = Number(delta.pnl_delta ?? 0);
      const durationDelta = Number(delta.duration_delta_bars ?? 0);
      if (Math.abs(pnlDelta) < 1e-9 && durationDelta === 0) return false;
    }
    if (typeof delta.pnl_delta === "number" && Math.abs(delta.pnl_delta) > 1e-9) return true;
    if (typeof delta.duration_delta_bars === "number" && delta.duration_delta_bars !== 0) return true;
    if (delta.changed === true || delta.is_changed === true) return true;
    if (delta.changed === false || delta.is_changed === false) return false;
  }

  if (!baseline) return false;

  const partialFields = ["exit_price", "net_pnl", "exit_reason"] as const;
  for (const field of partialFields) {
    if (comparableValue(baseline[field]) !== comparableValue(simulated[field])) {
      return true;
    }
  }

  const overlayFields: Array<keyof ExecutionTradeOverlay> = [
    "exit_time",
    "sl_price",
    "tp_price",
  ];
  for (const field of overlayFields) {
    if (comparableValue(baseline[field]) !== comparableValue(simulated[field])) {
      return true;
    }
  }
  return false;
}

export function buildSimulationChangeMap(
  baselineTrades: ExecutionTradeOverlay[] | undefined,
  simulatedTrades: SimulatedTradeRow[] | undefined
): Map<string, SimulationChangeInfo> {
  const baselineById = new Map<string, ExecutionTradeOverlay>();
  for (const t of baselineTrades ?? []) {
    const id = normalizeTradeOverlayId(t.trade_id);
    if (id) baselineById.set(id, t);
  }

  const out = new Map<string, SimulationChangeInfo>();
  for (const raw of simulatedTrades ?? []) {
    const { baseline: nestedBaseline, simulated, delta, tradeId } = unwrapSimulatedTradeRow(raw);
    const id = normalizeTradeOverlayId(tradeId ?? simulated.trade_id);
    if (!id) continue;
    const baseline = (nestedBaseline ?? baselineById.get(id)) as
      | (ExecutionTradeOverlay & Record<string, unknown>)
      | undefined;
    const changed = isSimulationTradeChanged(baseline, simulated, delta);
    out.set(id, { changed, baseline, simulated, delta });
  }
  return out;
}

export function mergeSimulationTradeRows(
  backtestRows: Array<Record<string, unknown>> | undefined,
  apiRows: SimulatedTradeRow[] | undefined
): SimulatedTradeRow[] {
  if (backtestRows?.length) {
    const apiById = new Map<string, SimulatedTradeRow>();
    for (const row of apiRows ?? []) {
      const id = normalizeTradeOverlayId(unwrapSimulatedTradeRow(row).tradeId);
      if (id) apiById.set(id, row);
    }
    return backtestRows.map((raw) => {
      const row = raw as unknown as SimulatedTradeRow;
      const id = normalizeTradeOverlayId(row.trade_id);
      const fromApi = id ? apiById.get(id) : undefined;
      if (!fromApi) return row;
      const bt = unwrapSimulatedTradeRow(row);
      const api = unwrapSimulatedTradeRow(fromApi);
      const btSim = (bt.simulated ?? {}) as Record<string, unknown>;
      const apiSim = (api.simulated ?? {}) as Record<string, unknown>;
      // Backtest owns simulated outcomes; execution-candles may only add chart geometry.
      const mergedSimulated = {
        ...apiSim,
        ...btSim,
        exit_time: apiSim.exit_time ?? btSim.exit_time,
        entry_time: apiSim.entry_time ?? btSim.entry_time,
        entry_price: apiSim.entry_price ?? btSim.entry_price,
        sl_price: "sl_price" in btSim ? btSim.sl_price : apiSim.sl_price ?? null,
        tp_price: "tp_price" in btSim ? btSim.tp_price : apiSim.tp_price ?? null,
      };
      return {
        ...row,
        ...fromApi,
        baseline: { ...(bt.baseline ?? {}), ...(api.baseline ?? {}) },
        simulated: mergedSimulated,
        delta: row.delta ?? fromApi.delta,
      } as SimulatedTradeRow;
    });
  }
  return apiRows ?? [];
}

export function executionCandleTypeSeconds(candleType: string | null | undefined): number {
  const n = String(candleType ?? "")
    .trim()
    .toLowerCase();
  if (n === "m" || n === "1m") return 60;
  if (n === "5m") return 300;
  if (n === "15m") return 900;
  if (n === "30m") return 1800;
  if (n === "h" || n === "1h") return 3600;
  if (n === "4h") return 14400;
  return 900;
}

function parseInstantMs(value: unknown): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }
  if (typeof value === "string") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }
  return null;
}

/** Estimate simulated exit time when BE only sends duration_delta_bars on the delta object. */
export function resolveSimulatedExitTime(
  info: SimulationChangeInfo,
  baselineTrade: ExecutionTradeOverlay | undefined,
  secondsPerCandle: number
): string | null {
  const direct = info.simulated.exit_time;
  if (direct) return String(direct);

  const entryMs =
    parseInstantMs(baselineTrade?.entry_time) ?? parseInstantMs(info.simulated.entry_time);
  const baselineExitMs =
    parseInstantMs(baselineTrade?.exit_time) ??
    parseInstantMs(info.baseline?.exit_time);
  const barsDelta = Number(info.delta?.duration_delta_bars ?? NaN);

  if (entryMs != null && baselineExitMs != null && Number.isFinite(barsDelta) && barsDelta !== 0) {
    const baselineSpanMs = Math.max(secondsPerCandle * 1000, baselineExitMs - entryMs);
    const baselineBars = Math.max(1, Math.round(baselineSpanMs / (secondsPerCandle * 1000)));
    const simBars = Math.max(1, baselineBars + barsDelta);
    return new Date(entryMs + simBars * secondsPerCandle * 1000).toISOString();
  }
  return null;
}

export function enrichSimulatedOverlayForChart(
  info: SimulationChangeInfo,
  baselineTrade: ExecutionTradeOverlay | undefined,
  secondsPerCandle: number
): ExecutionTradeOverlay & Record<string, unknown> {
  const simOnly = info.simulated;
  const exitTime = resolveSimulatedExitTime(info, baselineTrade, secondsPerCandle);
  return {
    ...(baselineTrade ?? {}),
    ...simOnly,
    trade_id: simOnly.trade_id ?? baselineTrade?.trade_id ?? null,
    exit_price: finitePrice(simOnly.exit_price),
    sl_price: finitePrice(simOnly.sl_price),
    tp_price: finitePrice(simOnly.tp_price),
    exit_time: exitTime,
    entry_time: baselineTrade?.entry_time ?? simOnly.entry_time ?? null,
    entry_price: baselineTrade?.entry_price ?? simOnly.entry_price ?? null,
    direction: baselineTrade?.direction ?? simOnly.direction ?? "long",
    exit_reason: simOnly.exit_reason ?? null,
  };
}
export function listChangedSimulatedTrades(
  baselineTrades: ExecutionTradeOverlay[] | undefined,
  simulatedTrades: SimulatedTradeRow[] | undefined,
  secondsPerCandle = 900
): Array<ExecutionTradeOverlay & Record<string, unknown>> {
  const changeMap = buildSimulationChangeMap(baselineTrades, simulatedTrades);
  const rows: Array<ExecutionTradeOverlay & Record<string, unknown>> = [];
  for (const info of Array.from(changeMap.values())) {
    if (!info.changed) continue;
    const baselineTrade = baselineTrades?.find(
      (t) =>
        normalizeTradeOverlayId(t.trade_id) === normalizeTradeOverlayId(info.simulated.trade_id)
    );
    rows.push(enrichSimulatedOverlayForChart(info, baselineTrade, secondsPerCandle));
  }
  return rows;
}

export function simulatedSlTpLevels(
  simulated: ExecutionTradeOverlay & Record<string, unknown>,
  simulatedLegs?: Array<"sl" | "tp"> | null
): {
  slPrice: number | null;
  tpPrice: number | null;
  exitPrice: number | null;
} {
  const exitPrice = finitePrice(simulated.exit_price);
  const entryPrice = finitePrice(simulated.entry_price);
  let sl = finitePrice(simulated.sl_price);
  let tp = finitePrice(simulated.tp_price);
  const reason = String(simulated.exit_reason ?? "").toLowerCase();
  const direction = String(simulated.direction ?? "").toLowerCase();

  if (simulatedLegs?.length) {
    if (!simulatedLegs.includes("sl")) sl = null;
    if (!simulatedLegs.includes("tp")) tp = null;
  }

  if (sl != null || tp != null) {
    return { slPrice: sl, tpPrice: tp, exitPrice };
  }

  // Infer the hit leg from exit geometry when reason is missing or inconsistent
  // (e.g. short profit exit below entry must not be drawn as SL).
  let hit: "sl" | "tp" | null =
    reason === "sl_hit" ? "sl" : reason === "tp_hit" ? "tp" : null;
  if (exitPrice != null && entryPrice != null) {
    const exitBelowEntry = exitPrice < entryPrice;
    const geometricTp = direction === "short" ? exitBelowEntry : !exitBelowEntry;
    if (hit === "sl" && geometricTp) hit = "tp";
    else if (hit === "tp" && !geometricTp) hit = "sl";
    else if (!hit) hit = geometricTp ? "tp" : "sl";
  }

  if (hit === "sl" && exitPrice != null) {
    return { slPrice: exitPrice, tpPrice: null, exitPrice };
  }
  if (hit === "tp" && exitPrice != null) {
    return { slPrice: null, tpPrice: exitPrice, exitPrice };
  }
  return { slPrice: sl, tpPrice: tp, exitPrice };
}
